import React, { forwardRef, useMemo } from 'react';
import { css } from '@emotion/react';
import {
  FloatingPortal,
  FloatingNode,
  FloatingOverlay,
  FloatingFocusManager,
  FloatingContext,
  ReferenceType,
} from '@floating-ui/react';
import { AnimatePresence, motion, type MotionProps } from 'motion/react';
import type { CommonHTMLAttributes } from '@frontend/types';
import { useThemeValues } from '../../hooks';
import { styles } from '../../styles';
import { DrawerHeader, DrawerContent } from './atoms';
import { drawerSizes, backdropVariants, createDrawerVariants, sideStyles, drawerBackdropId } from './constants';
import { DrawerContext } from './context';
import type { DrawerSide, DrawerSize } from './types';

type DrawerBaseProps = CommonHTMLAttributes & {
  /** IDs for a11y (dialog label and description) */
  labelId?: string;
  descriptionId?: string;
  /** Callback to close the drawer. */
  close?: () => void;
  /** Which side the drawer should appear from. */
  side?: DrawerSide;
  /** One of the predefined sizes. */
  size?: DrawerSize;
  /** Controls whether the drawer is open or closed. */
  isOpen?: boolean;
  /**
   * If true, the drawer stays mounted in the DOM at all times.
   * We then toggle between `hidden` and `visible` states,
   * rather than unmounting/remounting.
   */
  keepMounted?: boolean;

  /**
   * Whether to show the backdrop behind the drawer.
   */
  showBackdrop?: boolean;
  /** Provided by useFloating for focus management. */
  context: FloatingContext<ReferenceType>;
  children: React.ReactNode;
  zIndex?: number;
  additionalHeightOffset?: number;
} & MotionProps;

type DrawerPanelProps = CommonHTMLAttributes & {
  labelId?: string;
  descriptionId?: string;
  side: DrawerSide;
  size: DrawerSize;
  isOpen?: boolean;
  keepMounted?: boolean;
  children: React.ReactNode;
  zIndex?: number;
  additionalHeightOffset?: number;
} & MotionProps;

const DrawerPanel = forwardRef<HTMLDivElement, DrawerPanelProps>(
  (
    {
      labelId,
      descriptionId,
      isOpen,
      side,
      style,
      size,
      keepMounted,
      variants,
      children,
      zIndex: customZIndex,
      additionalHeightOffset = 0,
      ...rest
    },
    ref
  ) => {
    const { colors, spacing, zIndex: zIndexDefault, heightOffset } = useThemeValues();

    const dimensionStyles = useMemo(
      () => ({
        width: side === 'left' || side === 'right' ? drawerSizes[size] : 'auto',
        height:
          side === 'top' || side === 'bottom'
            ? drawerSizes[size]
            : !!heightOffset || !!additionalHeightOffset
            ? `calc(100% - ${heightOffset + additionalHeightOffset}px)`
            : '100%',
        maxWidth: side === 'left' || side === 'right' ? drawerSizes[size] : '100%',
      }),
      [side, size, heightOffset, additionalHeightOffset]
    );

    const selectedSideStyles = useMemo(
      () => sideStyles(heightOffset + additionalHeightOffset)[side],
      [heightOffset, side, additionalHeightOffset]
    );

    return (
      <motion.div
        ref={ref}
        key='drawer-panel'
        initial='hidden'
        animate={isOpen ? 'visible' : 'hidden'}
        exit='exit'
        variants={variants}
        role='dialog'
        aria-modal='true'
        aria-labelledby={labelId}
        aria-describedby={descriptionId}
        css={[
          css`
            background: ${colors.white};
            position: absolute;
            z-index: ${customZIndex ?? zIndexDefault.modals};
            display: flex;
            flex-direction: column;
            padding: ${spacing(4)};
            gap: ${spacing(2)};
          `,
          selectedSideStyles,
          dimensionStyles,
          keepMounted &&
            css`
              position: fixed;
              bottom: 0;
            `,
        ]}
        {...rest}
      >
        {children}
      </motion.div>
    );
  }
);

DrawerPanel.displayName = 'DrawerPanel';

const MotionOverlay = motion(FloatingOverlay);

const Overlay = ({ zIndex, additionalHeightOffset = 0 }: { zIndex?: number; additionalHeightOffset?: number }) => (
  <MotionOverlay
    id={drawerBackdropId}
    lockScroll
    initial='hidden'
    animate='visible'
    exit='exit'
    variants={backdropVariants}
    css={(theme) => [
      css`
        z-index: ${zIndex ?? theme.zIndex.modals};
        background-color: rgba(0, 33, 82, 0.3);
        position: fixed;
        top: 0;
        height: 100%;
      `,
      styles.getAdjustedHeight(theme, undefined, undefined, additionalHeightOffset),
    ]}
  />
);

Overlay.displayName = 'Overlay';

type DrawerOverlayProps = {
  isOpen?: boolean;
  keepMounted: boolean;
  children: React.ReactNode;
  zIndex?: number;
  additionalHeightOffset?: number;
  showBackdrop?: boolean;
};

const DrawerOverlay = ({
  isOpen,
  keepMounted,
  children,
  zIndex,
  additionalHeightOffset,
  showBackdrop,
}: DrawerOverlayProps) => {
  if (keepMounted) {
    return (
      <>
        {showBackdrop && (
          <AnimatePresence>
            {isOpen && <Overlay zIndex={zIndex} additionalHeightOffset={additionalHeightOffset} />}
          </AnimatePresence>
        )}
        {children}
      </>
    );
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {showBackdrop && <Overlay zIndex={zIndex} additionalHeightOffset={additionalHeightOffset} />}
          {children}
        </>
      )}
    </AnimatePresence>
  );
};

export const DrawerBase = forwardRef<HTMLDivElement, DrawerBaseProps>(
  (
    {
      labelId,
      descriptionId,
      close,
      side = 'right',
      size = 'small',
      isOpen,
      keepMounted = false,
      showBackdrop = true,
      context,
      children,
      ...rest
    },
    ref
  ) => {
    const variants = useMemo(() => createDrawerVariants(keepMounted)[side], [keepMounted, side]);

    return (
      <FloatingFocusManager context={context} disabled={!isOpen}>
        <DrawerOverlay
          zIndex={rest?.zIndex}
          additionalHeightOffset={rest?.additionalHeightOffset}
          isOpen={isOpen}
          keepMounted={keepMounted}
          showBackdrop={showBackdrop}
        >
          {isOpen ? (
            <DrawerContext.Provider value={{ labelId, descriptionId, close }}>
              <DrawerPanel
                ref={ref}
                labelId={labelId}
                descriptionId={descriptionId}
                isOpen={isOpen}
                side={side}
                size={size}
                variants={variants}
                {...rest}
              >
                {children}
              </DrawerPanel>
            </DrawerContext.Provider>
          ) : (
            <DrawerContext.Provider value={{ labelId, descriptionId, close }}>
              <DrawerPanel
                ref={ref}
                labelId={labelId}
                descriptionId={descriptionId}
                isOpen={isOpen}
                side={side}
                size={size}
                variants={variants}
                {...rest}
              >
                {children}
              </DrawerPanel>
            </DrawerContext.Provider>
          )}
        </DrawerOverlay>
      </FloatingFocusManager>
    );
  }
);

DrawerBase.displayName = 'DrawerBase';

interface DrawerProps extends DrawerBaseProps {
  nodeId?: string;
  rootId?: string;
  root?: HTMLElement | null | React.MutableRefObject<HTMLElement | null>;
}

export const DrawerOptional = forwardRef<HTMLDivElement, DrawerProps>(
  ({ nodeId, rootId, root, isOpen, context, ...props }, ref) => {
    return (
      <FloatingNode id={nodeId}>
        <FloatingPortal id={rootId} root={root}>
          <DrawerBase ref={ref} isOpen={isOpen} context={context} {...props} />
        </FloatingPortal>
      </FloatingNode>
    );
  }
);

DrawerOptional.displayName = 'DrawerOptional';

const DrawerNamespace = Object.assign(DrawerOptional, {
  Header: DrawerHeader,
  Content: DrawerContent,
});

export { DrawerNamespace as Drawer };
